import type { AttachedExperienceReference, CampaignConfig, CampaignSchedule, QuizConfig, QuizQuestion, QuizRound, QuizSlot, QuizStage, ScreenBrandColorMap, ScreenBrandColors, StagedAssets } from '@/types/form.types'
import type { ManagedCampaignEditDraft } from '@/services/campaigns'
import { getDefaultTemplateIdForExperienceMode } from '@/config/quiz-templates.config'
import { DEFAULT_BUTTON_CONFIGS } from '@/utils/buttonStyles'

type BuilderDraftState = {
  template: CampaignConfig
  lookFeel: {
    brandColors: ScreenBrandColors
    screenBrandColors: ScreenBrandColorMap
    googleFont: string
    uploadedAssets: StagedAssets
    stagedAssets: StagedAssets
    selectedDefaultAssets: {
      logo: string | null
      mapMarker: string | null
      ar: string | null
      registrationBackground: string | null
      prizeBackground: string | null
    }
  }
  drops: {
    dropsPerDrop: number
    maxClaimsPerUser: number
    timePeriod: number
    winRatio: number
  }
  message: {
    text: string
    maxLength: number
  }
  schedule: CampaignSchedule
}

const DEFAULT_BRAND_COLORS: ScreenBrandColors = {
  primary: '#7ae6b1',
  secondary: '#7ae6b1',
  background: '#292e50',
  header: '#181b2f',
  buttonText: '#ffffff',
}

const DEFAULT_SCREEN_COLORS: ScreenBrandColorMap = {
  registration: { ...DEFAULT_BRAND_COLORS },
  interaction: { ...DEFAULT_BRAND_COLORS },
  prize: { ...DEFAULT_BRAND_COLORS },
}

const rgbaToHex = (value?: string | null, fallback = '#ffffff'): string => {
  if (!value) return fallback
  if (value.startsWith('#')) return value
  const match = value.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i)
  if (!match) return fallback
  return `#${[match[1], match[2], match[3]].map((part) => Number(part).toString(16).padStart(2, '0')).join('')}`
}

const toTimeValue = (value?: string | null): string | null => {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

const toDateValue = (value?: string | null): string | null => {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

const buildUploadedAsset = (url?: string | null) => {
  if (!url) return undefined
  return {
    s3Url: url,
    s3Path: url,
    fileName: url.split('/').pop() || 'asset',
    fileSize: 0,
    fileType: '',
    uploadTimestamp: new Date(),
    bucketType: 'approved' as const,
  }
}

const mapQuestion = (question: any): QuizQuestion => ({
  id: question.id,
  text: question.text || question.prompt?.text || '',
  choices: Array.isArray(question.choices) ? question.choices.map((choice: any) => ({
    id: choice.id,
    label: choice.label,
    imageAssetId: choice.imageAssetId,
  })) : [],
  correctChoiceId: question.correctChoiceId,
  correctChoiceIds: Array.isArray(question.correctChoiceIds) ? [...question.correctChoiceIds] : undefined,
  type: question.type === 'single-select' && Array.isArray(question.correctChoiceIds) && question.correctChoiceIds.length > 1
    ? 'multi-select'
    : (question.type || 'single-select'),
  promptFormat: question.promptFormat || ((question.prompt?.videoAssetId && 'video') || (question.prompt?.imageAssetId && 'image') || 'text'),
  prompt: question.prompt ? { ...question.prompt } : undefined,
  ratingScale: question.ratingScale ? { ...question.ratingScale } : undefined,
  booleanLabels: question.booleanLabels ? { ...question.booleanLabels } : undefined,
  validation: question.validation ? { ...question.validation } : undefined,
})

type LegacyRoundConfig = {
  id: string
  label?: string
  releaseType?: 'immediate' | 'offset' | 'date'
  offsetDays?: number
  releaseDate?: string
  releaseTime?: string
}

const mapQuizConfig = (draft: ManagedCampaignEditDraft): QuizConfig => {
  const questionPack = draft.questionPack as any
  const attachedExperience = draft.attachedExperience as AttachedExperienceReference | null | undefined
  const experience = questionPack?.experience || {}
  const canonicalRounds = Array.isArray(experience.rounds) ? experience.rounds : []
  const legacyRoundsById = new Map<string, LegacyRoundConfig>(
    Array.isArray(questionPack?.rounds)
      ? questionPack.rounds.map((round: any): [string, LegacyRoundConfig] => [round.id, round as LegacyRoundConfig])
      : []
  )
  const scheduleSlots = Array.isArray(experience?.schedule?.slots) ? experience.schedule.slots : []

  const rounds: QuizRound[] = canonicalRounds.map((round: any, roundIndex: number) => ({
    id: round.id || `round-${roundIndex + 1}`,
    label: round.label || `Round ${roundIndex + 1}`,
    stages: Array.isArray(round.stages) ? round.stages
      .map((stage: any, stageIndex: number): QuizStage => ({
        id: stage.id || `${round.id || `round-${roundIndex + 1}`}-stage-${stageIndex + 1}`,
        order: stage.order || stageIndex + 1,
        type: stage.type || stage.questionSet?.mode || 'quiz',
        questions: Array.isArray(stage.questionSet?.questions) ? stage.questionSet.questions.map(mapQuestion) : [],
      }))
      .sort((a: QuizStage, b: QuizStage) => a.order - b.order) : [],
  }))

  const slots: QuizSlot[] = scheduleSlots.map((slot: any, index: number) => {
    const legacyRound = legacyRoundsById.get(slot.roundId)
    return {
      id: slot.id || `slot-${slot.roundId || index + 1}`,
      label: slot.label || legacyRound?.label || `Round ${index + 1}`,
      roundId: slot.roundId,
      releaseType: legacyRound?.releaseType || (index === 0 ? 'immediate' : 'offset'),
      offsetDays: legacyRound?.offsetDays,
      releaseDate: legacyRound?.releaseDate,
      releaseTime: slot.releaseTime || legacyRound?.releaseTime,
    }
  })

  const settings = questionPack?.settings || {}
  const leaderboard = experience?.leaderboard
    ? {
        enabled: experience.leaderboard.enabled === true,
        eventName: experience.leaderboard.eventName || 'quiz.completed.score',
        displayMode: 'top-10-plus-user' as const,
        title: experience.leaderboard.title || 'Top Players',
        emptyStateText: experience.leaderboard.emptyStateText || 'Be the first to post a score.',
        showAfterCompletionOnly: experience.leaderboard.showAfterCompletionOnly !== false,
      }
    : {
        enabled: false,
        eventName: 'quiz.completed.score',
        displayMode: 'top-10-plus-user' as const,
        title: 'Top Players',
        emptyStateText: 'Be the first to post a score.',
        showAfterCompletionOnly: true,
      }

  return {
    experienceMode: experience.type || 'quiz',
    quizTemplateId: questionPack?.quizTemplateId || getDefaultTemplateIdForExperienceMode(experience.type || 'quiz'),
    experienceTemplateFamilyId: attachedExperience?.templateFamilyId || questionPack?.experienceTemplateFamilyId,
    experienceTemplateVersionId: questionPack?.experienceTemplateVersionId,
    experienceDefinitionId: attachedExperience?.lineageId || questionPack?.experienceDefinitionId,
    experienceDefinitionVersionId: attachedExperience?.versionId || questionPack?.experienceDefinitionVersionId,
    experienceDefinitionName: attachedExperience?.name || questionPack?.experienceDefinitionName,
    experienceDefinitionStatus: attachedExperience?.status || questionPack?.experienceDefinitionStatus,
    experienceDefinitionLocalKey: attachedExperience?.localKey || attachedExperience?.experienceId || questionPack?.experienceDefinitionLocalKey,
    experienceDefinitionLastSavedAt: questionPack?.experienceDefinitionLastSavedAt,
    timeLimitSeconds: settings.timeLimitSeconds || 60,
    passThreshold: settings.passThreshold || 1,
    attemptLimit: settings.attemptLimit || 1,
    attemptWindow: settings.attemptWindow || 'week',
    attemptWindowRolloverTime: settings.attemptWindowRolloverTime,
    attemptWindowRolloverWeekday: settings.attemptWindowRolloverWeekday,
    rounds,
    slots,
    noActiveSlotAction: experience?.schedule?.noActiveSlot?.action || null,
    leaderboard,
    primaryColor: settings.primaryColor || '#7ae6b1',
    backgroundColor: settings.backgroundColor || '#292e50',
    correctColor: settings.correctColor || '#22c55e',
    incorrectColor: settings.incorrectColor || '#ef4444',
  }
}

export const buildBuilderDraftState = (draft: ManagedCampaignEditDraft): BuilderDraftState => {
  const campaign = draft.campaign as Record<string, any>
  const registrationColors: ScreenBrandColors = {
    primary: rgbaToHex(campaign.secondaryColor, DEFAULT_BRAND_COLORS.primary),
    secondary: rgbaToHex(campaign.secondaryColor, DEFAULT_BRAND_COLORS.secondary),
    background: rgbaToHex(campaign.backgroundColor, DEFAULT_BRAND_COLORS.background),
    header: rgbaToHex(campaign.headerColor, DEFAULT_BRAND_COLORS.header),
    buttonText: rgbaToHex(campaign.buttonTextColor, DEFAULT_BRAND_COLORS.buttonText),
  }

  return {
    template: {
      feature: 'quiz',
      service: 'tap-to-redeem',
      experienceType: 'quiz',
      redeemType: 'Tap to collect',
      campaignName: campaign.name || '',
      campaignDescription: campaign.description || '',
      googleFont: campaign.googleFont || 'Montserrat',
      logoUrl: campaign.logo || '',
      faviconUrl: campaign.favicon || '',
      assetsApproved: true,
      campaignBusinessTemplate: campaign.campaignBusinessTemplate || 'irl-template-001d-freemium',
      buttonConfigs: {
        registration: { ...DEFAULT_BUTTON_CONFIGS.registration },
        interaction: { ...DEFAULT_BUTTON_CONFIGS.interaction },
        prize: { ...DEFAULT_BUTTON_CONFIGS.prize },
      },
      attachedExperience: draft.attachedExperience || null,
      quizConfig: mapQuizConfig(draft),
      orgId: campaign.orgId || '',
      tenantId: campaign.tenantId || '',
    },
    lookFeel: {
      brandColors: { ...registrationColors },
      screenBrandColors: {
        registration: { ...registrationColors },
        interaction: { ...registrationColors },
        prize: { ...registrationColors },
      },
      googleFont: campaign.googleFont || 'Montserrat',
      uploadedAssets: {
        logo: buildUploadedAsset(campaign.logo),
        registrationBackground: buildUploadedAsset(campaign.backgroundImageUrl),
      },
      stagedAssets: {},
      selectedDefaultAssets: {
        logo: null,
        mapMarker: null,
        ar: null,
        registrationBackground: null,
        prizeBackground: null,
      },
    },
    drops: {
      dropsPerDrop: Number(campaign.dropsPerDrop) || 1,
      maxClaimsPerUser: Number(campaign.maxClaimsPerUser) || 3,
      timePeriod: Number(campaign.timePeriod) || 24,
      winRatio: Number(campaign.winRatio) || 50,
    },
    message: {
      text: campaign.prizeMessage || '',
      maxLength: 280,
    },
    schedule: {
      startTime: toTimeValue(campaign.startDate),
      startDate: toDateValue(campaign.startDate),
      startDateTime: campaign.startDate || null,
      endTime: toTimeValue(campaign.endDate),
      endDate: toDateValue(campaign.endDate),
      endDateTime: campaign.endDate || null,
      timezone: campaign.timezone || 'UTC',
    },
  }
}
